import { modelTypeArea } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { SlideInferenceResults } from 'interfaces/calculateFeatures';
import { flatMap, forEach, keyBy, pickBy, some, uniq, uniqBy } from 'lodash';
import { getModelId, ModelPreferencesById } from 'utils/models';
import { OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { SlideRegistrationDetailsByOrchestration } from 'utils/slideRegistrations';
import { ClassConfigs } from './classConfigFromSelections';

export type AreaTransferTargetsBySourceStain = Record<string, string[]>;

export interface RegisteredAreaOption {
  id: string;
  area: string;
  sourceStain: string;
}

export const getTransferTargetsBySourceStain = (
  slides: SlideInferenceResults[],
  selectedRegistrations: SlideRegistrationDetailsByOrchestration
): AreaTransferTargetsBySourceStain => {
  const slidesById = keyBy(slides, 'slideId');
  const targetStainsBySourceStain: AreaTransferTargetsBySourceStain = {};

  forEach(flatMap(selectedRegistrations), (registration) => {
    const sourceStain = slidesById[registration.sourceSlideId]?.stainingType;
    const targetStain = slidesById[registration.targetSlideId]?.stainingType;

    if (!sourceStain || !targetStain || sourceStain === targetStain) {
      return;
    }

    targetStainsBySourceStain[sourceStain] = uniq([...(targetStainsBySourceStain[sourceStain] ?? []), targetStain]);
    targetStainsBySourceStain[targetStain] = uniq([...(targetStainsBySourceStain[targetStain] ?? []), sourceStain]);
  });

  return targetStainsBySourceStain;
};

export const getEnabledAreaTransferTargetsBySourceStain = (
  slides: SlideInferenceResults[],
  selectedOrchestrations: OrchestrationBySlideByType,
  selectedRegistrations: SlideRegistrationDetailsByOrchestration,
  modelPreferencesByModelId: ModelPreferencesById
): AreaTransferTargetsBySourceStain => {
  const shouldTransferAreasByDefault = uniq(flatMap(slides, 'stainingType')).length > 1;
  const transferTargetsBySourceStain = getTransferTargetsBySourceStain(slides, selectedRegistrations);

  return pickBy(transferTargetsBySourceStain, (_targetStains, sourceStain) =>
    some(slides, (slide) => {
      if (slide.stainingType !== sourceStain) {
        return false;
      }

      const selectedAreaResult = selectedOrchestrations[slide.slideId]?.[modelTypeArea.apiModelValue];
      const modelId = selectedAreaResult?.model?.modelId ?? getModelId(selectedAreaResult?.model?.modelUrl);
      return (
        Boolean(selectedAreaResult) &&
        (modelPreferencesByModelId[modelId]?.transferAreasToRegisteredStains ?? shouldTransferAreasByDefault)
      );
    })
  );
};

export const getRegisteredAreaOptionsByTargetStain = (
  areaTransferTargetsBySourceStain: AreaTransferTargetsBySourceStain,
  classConfig: ClassConfigs
): Record<string, RegisteredAreaOption[]> => {
  const registeredAreaOptionsByTargetStain: Record<string, RegisteredAreaOption[]> = {};

  forEach(areaTransferTargetsBySourceStain, (targetStains, sourceStain) => {
    const sourceAreas = classConfig[sourceStain]?.area ?? [];

    forEach(targetStains, (targetStain) => {
      registeredAreaOptionsByTargetStain[targetStain] = uniqBy(
        [
          ...(registeredAreaOptionsByTargetStain[targetStain] ?? []),
          ...sourceAreas.map((area) => ({
            id: `${area}_from_${sourceStain}`,
            area: String(area),
            sourceStain,
          })),
        ],
        'id'
      );
    });
  });

  return registeredAreaOptionsByTargetStain;
};
